import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Alert, Switch, FormControlLabel,
  Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, IconButton, Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon, Security as SecurityIcon, PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon, Block as BlockIcon, ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { preferencesApi, authApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import type { PlanningPreference } from '../types/models';

// MFA Setup Section
function MfaSetupSection() {
  const { user } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null);
  const [setupData, setSetupData] = useState<{ sharedKey: string; authenticatorUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    authApi.me().then(data => setMfaEnabled(data.mfaEnabled)).catch(() => {});
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.setupMfa();
      setSetupData(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.enableMfa(code);
      setRecoveryCodes(data.recoveryCodes);
      setMfaEnabled(true);
      setSuccess('MFA enabled successfully');
      setSetupData(null);
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyKey = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (mfaEnabled === null) return null;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <SecurityIcon />
          <Typography variant="h6">Multi-Factor Authentication</Typography>
          {mfaEnabled && <Chip label="Enabled" color="success" size="small" icon={<CheckCircleIcon />} />}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {mfaEnabled && !recoveryCodes && (
          <Typography color="text.secondary">
            MFA is enabled on your account. Your login requires a verification code from your authenticator app.
          </Typography>
        )}

        {!mfaEnabled && !setupData && (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Add an extra layer of security to your account by enabling MFA with an authenticator app (Microsoft Authenticator, Google Authenticator, etc.).
            </Typography>
            <Button variant="outlined" onClick={handleSetup} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SecurityIcon />}>
              Set Up MFA
            </Button>
          </Box>
        )}

        {setupData && (
          <Box>
            <Typography sx={{ mb: 1 }}>
              Scan this key in your authenticator app, or enter it manually:
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TextField
                value={setupData.sharedKey}
                fullWidth
                size="small"
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', letterSpacing: 2 } }}
              />
              <Tooltip title="Copy key">
                <IconButton onClick={() => copyKey(setupData.sharedKey)} size="small"><CopyIcon /></IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              App name: <strong>Cadence</strong> &middot; Account: <strong>{user?.email}</strong>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ mb: 1 }}>Enter the 6-digit code from your authenticator app to verify:</Typography>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                size="small"
                inputProps={{ maxLength: 6, style: { fontFamily: 'monospace', fontSize: 18, letterSpacing: 8, textAlign: 'center' } }}
                sx={{ width: 180 }}
              />
              <Button variant="contained" onClick={handleEnable} disabled={loading || code.length !== 6}>
                {loading ? 'Verifying...' : 'Enable MFA'}
              </Button>
            </Stack>
          </Box>
        )}

        {recoveryCodes && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Save these recovery codes in a safe place. Each code can only be used once to sign in if you lose access to your authenticator app.
            </Alert>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace' }}>
              {recoveryCodes.map((rc, i) => (
                <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>{rc}</Typography>
              ))}
            </Box>
            <Button size="small" sx={{ mt: 1 }} onClick={() => copyKey(recoveryCodes.join('\n'))}>
              Copy All Codes
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// User Management Section
function UserManagementSection() {
  const { user } = useAuth();
  const [users, setUsers] = useState<Array<{
    id: string; email: string; displayName: string; role: string;
    isActive: boolean; createdAt: string; lastLoginAt: string | null; mfaEnabled: boolean;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', role: 'ExecutiveAssistant' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      const data = await authApi.getUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.status === 403 ? 'Insufficient permissions' : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleToggleActive = async (id: string) => {
    try {
      await authApi.toggleUserActive(id);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let pw = '';
    const arr = new Uint8Array(20);
    crypto.getRandomValues(arr);
    for (let i = 0; i < 20; i++) pw += chars[arr[i] % chars.length];
    return pw;
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.displayName || !newUser.password) {
      setAddError('All fields are required');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await authApi.registerUser({
        email: newUser.email,
        password: newUser.password,
        displayName: newUser.displayName,
        tenantId: user?.tenantId || '',
        role: newUser.role,
      });
      setShowAdd(false);
      setNewUser({ email: '', password: '', displayName: '', role: 'ExecutiveAssistant' });
      loadUsers();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      setAddError(errors ? (Array.isArray(errors) ? errors.join(', ') : String(errors)) : 'Failed to create user');
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) return null;
  if (error && users.length === 0) return null; // Hide section if no permission

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonAddIcon />
            <Typography variant="h6">User Management</Typography>
          </Stack>
          <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} onClick={() => setShowAdd(true)}>
            Add User
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stack spacing={1}>
          {users.map(u => (
            <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  {u.displayName}
                  {u.id === user?.id && <Chip label="You" size="small" sx={{ ml: 1 }} />}
                </Typography>
                <Typography variant="body2" color="text.secondary">{u.email}</Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip label={u.role} size="small" variant="outlined" />
                {u.mfaEnabled && <Chip label="MFA" size="small" color="success" />}
                {!u.isActive && <Chip label="Inactive" size="small" color="error" />}
                {u.id !== user?.id && (
                  <Tooltip title={u.isActive ? 'Deactivate user' : 'Reactivate user'}>
                    <IconButton size="small" onClick={() => handleToggleActive(u.id)}>
                      {u.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>

        {/* Add User Dialog */}
        <Dialog open={showAdd} onClose={() => setShowAdd(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New User</DialogTitle>
          <DialogContent>
            {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Display Name" fullWidth value={newUser.displayName} onChange={e => setNewUser(prev => ({ ...prev, displayName: e.target.value }))} />
              <TextField label="Email" type="email" fullWidth value={newUser.email} onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))} />
              <Stack direction="row" spacing={1}>
                <TextField label="Password" fullWidth value={newUser.password} onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))} helperText="Min 8 chars, upper, lower, digit, special" />
                <Button variant="text" size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => setNewUser(prev => ({ ...prev, password: generatePassword() }))}>Generate</Button>
              </Stack>
              <TextField label="Role" select fullWidth value={newUser.role} onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value }))}>
                <MenuItem value="Executive">Executive</MenuItem>
                <MenuItem value="ExecutiveAssistant">Executive Assistant</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAddUser} disabled={addLoading}>
              {addLoading ? 'Creating...' : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// Main Preferences Page
export default function PreferencesPage() {
  const [prefs, setPrefs] = useState<Partial<PlanningPreference>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await preferencesApi.get();
        setPrefs(data);
      } catch (err) {
        console.error('Failed to load preferences', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Only send fields matching UpdatePreferencesRequest, with TimeOnly format
      const toTimeOnly = (v?: string) => v ? (v.length === 5 ? `${v}:00` : v) : undefined;
      const payload = {
        workDayStart: toTimeOnly(prefs.workDayStart),
        workDayEnd: toTimeOnly(prefs.workDayEnd),
        meetingBufferMinutes: prefs.meetingBufferMinutes,
        meetingPrepMinutes: prefs.meetingPrepMinutes,
        maxFocusBlockMinutes: prefs.maxFocusBlockMinutes,
        minBlockMinutes: prefs.minBlockMinutes,
        lunchStartMinute: prefs.lunchStartMinute,
        lunchDurationMinutes: prefs.lunchDurationMinutes,
        icsCalendarUrl: prefs.icsCalendarUrl || undefined,
        treatPrivateAsPriority: prefs.treatPrivateAsPriority,
        preferredAiModel: prefs.preferredAiModel || undefined,
      };
      await preferencesApi.update(payload);
      setMessage({ type: 'success', text: 'Preferences saved' });
    } catch (err: any) {
      const data = err.response?.data;
      let msg = 'Failed to save';
      if (typeof data === 'string') msg = data;
      else if (data?.title) msg = data.title;
      else if (data?.errors) {
        const errs = Object.values(data.errors).flat();
        msg = errs.join(', ');
      } else if (data?.error) msg = data.error;
      else if (err.message) msg = err.message;
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Settings</Typography>
          <Typography variant="subtitle1">Manage your account security, users, and preferences</Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Stack spacing={2.5}>
        {/* MFA Setup */}
        <MfaSetupSection />

        {/* User Management */}
        <UserManagementSection />

        <Divider />

        {/* Working Hours */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Working Hours</Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="Day Start" type="time" fullWidth value={prefs.workDayStart || '08:00'} onChange={e => setPrefs(prev => ({ ...prev, workDayStart: e.target.value }))} InputLabelProps={{ shrink: true }} />
              <TextField label="Day End" type="time" fullWidth value={prefs.workDayEnd || '18:00'} onChange={e => setPrefs(prev => ({ ...prev, workDayEnd: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <TextField label="Lunch Start (minute of day)" type="number" fullWidth value={prefs.lunchStartMinute || 720} onChange={e => setPrefs(prev => ({ ...prev, lunchStartMinute: parseInt(e.target.value) }))} helperText="720 = 12:00" />
              <TextField label="Lunch Duration (min)" type="number" fullWidth value={prefs.lunchDurationMinutes || 60} onChange={e => setPrefs(prev => ({ ...prev, lunchDurationMinutes: parseInt(e.target.value) }))} />
            </Stack>
          </CardContent>
        </Card>

        {/* Scheduling */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Scheduling Rules</Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="Meeting Buffer (min)" type="number" fullWidth value={prefs.meetingBufferMinutes ?? 10} onChange={e => setPrefs(prev => ({ ...prev, meetingBufferMinutes: parseInt(e.target.value) }))} helperText="Buffer after each meeting" />
              <TextField label="Meeting Prep (min)" type="number" fullWidth value={prefs.meetingPrepMinutes ?? 5} onChange={e => setPrefs(prev => ({ ...prev, meetingPrepMinutes: parseInt(e.target.value) }))} helperText="Prep time before meetings" />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <TextField label="Max Focus Block (min)" type="number" fullWidth value={prefs.maxFocusBlockMinutes ?? 90} onChange={e => setPrefs(prev => ({ ...prev, maxFocusBlockMinutes: parseInt(e.target.value) }))} helperText="Longest uninterrupted work block" />
              <TextField label="Min Block (min)" type="number" fullWidth value={prefs.minBlockMinutes ?? 15} onChange={e => setPrefs(prev => ({ ...prev, minBlockMinutes: parseInt(e.target.value) }))} helperText="Shortest schedulable block" />
            </Stack>
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Calendar Integration</Typography>
            <TextField
              label="ICS Calendar URL"
              fullWidth
              value={prefs.icsCalendarUrl || ''}
              onChange={e => setPrefs(prev => ({ ...prev, icsCalendarUrl: e.target.value }))}
              placeholder="https://outlook.office365.com/owa/calendar/..."
              helperText="Microsoft 365 ICS feed URL"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={prefs.treatPrivateAsPriority ?? true}
                  onChange={e => setPrefs(prev => ({ ...prev, treatPrivateAsPriority: e.target.checked }))}
                />
              }
              label="Treat private calendar events as priority meetings"
              sx={{ mt: 1 }}
            />
          </CardContent>
        </Card>

        {/* AI */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>AI Configuration</Typography>
            <TextField
              label="Preferred AI Model"
              fullWidth
              value={prefs.preferredAiModel || ''}
              onChange={e => setPrefs(prev => ({ ...prev, preferredAiModel: e.target.value }))}
              placeholder="gpt-4o"
              helperText="OpenAI model used for planning and image analysis"
            />
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
